import { AdminAuthPanel } from "@cavort/auth";
import {
	RiBankCardLine,
	RiCheckLine,
	RiCloseLine,
	RiExternalLinkLine,
	RiHistoryLine,
	RiMailLine,
	RiShieldUserLine,
} from "@remixicon/react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { $api } from "@/api/query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchCurrentUser } from "@/lib/auth";

export const Route = createFileRoute("/_app/admin")({
	beforeLoad: async () => {
		const user = await fetchCurrentUser();
		if (!user?.isAdmin) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: AdminPage,
});

type Tab = "auth" | "audit" | "stripe" | "mail";

const tabs = [
	{ key: "auth" as const, label: "Auth", icon: RiShieldUserLine },
	{ key: "audit" as const, label: "Audit Log", icon: RiHistoryLine },
	{ key: "stripe" as const, label: "Stripe", icon: RiBankCardLine },
	{ key: "mail" as const, label: "Mail", icon: RiMailLine },
];

function AdminPage() {
	const [tab, setTab] = useState<Tab>("auth");

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">Admin Panel</h1>
				<p className="text-muted-foreground">System-wide configuration and management.</p>
			</div>

			<div className="flex gap-1 border-b overflow-x-auto">
				{tabs.map((t) => (
					<button
						key={t.key}
						type="button"
						onClick={() => setTab(t.key)}
						className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
							tab === t.key
								? "border-primary text-primary"
								: "border-transparent text-muted-foreground hover:text-foreground"
						}`}
					>
						<t.icon className="size-4" />
						{t.label}
					</button>
				))}
			</div>

			{tab === "auth" && <AdminAuthPanel />}
			{tab === "audit" && <AuditTab />}
			{tab === "stripe" && <StripeTab />}
			{tab === "mail" && <MailTab />}
		</div>
	);
}

// ── Audit Tab ────────────────────────────────────────────────────────

function AuditTab() {
	const { data, isLoading } = $api.useQuery("get", "/api/v1/admin/audit-log", {
		params: { query: { limit: 50, offset: 0 } },
	});

	const entries = data?.data ?? [];

	if (isLoading) return <p className="text-muted-foreground">Loading...</p>;

	return (
		<div className="border rounded-lg overflow-hidden">
			<table className="w-full text-sm">
				<thead className="bg-muted/50">
					<tr>
						<th className="text-left p-3 font-medium">Action</th>
						<th className="text-left p-3 font-medium">User</th>
						<th className="text-left p-3 font-medium">Resource</th>
						<th className="text-left p-3 font-medium">Time</th>
					</tr>
				</thead>
				<tbody>
					{entries.map((e) => (
						<tr key={e.id} className="border-t">
							<td className="p-3">
								<Badge variant="outline">{e.action}</Badge>
							</td>
							<td className="p-3 text-muted-foreground">{e.userEmail ?? "-"}</td>
							<td className="p-3 text-muted-foreground">
								{e.resourceType ? `${e.resourceType}/${e.resourceId ?? "?"}` : "-"}
							</td>
							<td className="p-3 text-muted-foreground">
								{new Date(e.createdAt).toLocaleString()}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			{entries.length === 0 && (
				<p className="text-center py-8 text-muted-foreground">No audit log entries yet</p>
			)}
		</div>
	);
}

// ── Stripe Tab ───────────────────────────────────────────────────────

function StripeTab() {
	const [stripeSecretKey, setStripeSecretKey] = useState("");
	const [stripeWebhookSecret, setStripeWebhookSecret] = useState("");
	const [stripePublishableKey, setStripePublishableKey] = useState("");
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);
	const [testResult, setTestResult] = useState<{
		ok: boolean;
		message: string;
	} | null>(null);

	async function handleTestConnection() {
		setTesting(true);
		setTestResult(null);
		try {
			const res = await fetch("/api/v1/admin/stripe/test", {
				method: "POST",
				credentials: "include",
			});
			const data = await res.json().catch(() => null);
			if (res.ok) {
				setTestResult({
					ok: true,
					message: data?.message ?? "Connection successful",
				});
			} else {
				setTestResult({
					ok: false,
					message: data?.detail ?? "Connection failed",
				});
			}
		} catch {
			setTestResult({ ok: false, message: "Failed to reach server" });
		} finally {
			setTesting(false);
		}
	}

	async function handleSave() {
		setSaving(true);
		try {
			const res = await fetch("/api/v1/admin/stripe/config", {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({
					secret_key: stripeSecretKey || undefined,
					webhook_secret: stripeWebhookSecret || undefined,
					publishable_key: stripePublishableKey || undefined,
				}),
			});
			if (res.ok) {
				toast.success("Stripe configuration saved");
				setStripeSecretKey("");
				setStripeWebhookSecret("");
				setStripePublishableKey("");
			} else {
				const data = await res.json().catch(() => null);
				toast.error(data?.detail ?? "Failed to save");
			}
		} finally {
			setSaving(false);
		}
	}

	return (
		<div className="space-y-6 max-w-2xl">
			<Card>
				<CardHeader>
					<div className="flex items-center gap-2">
						<RiBankCardLine className="size-5 text-muted-foreground" />
						<div>
							<CardTitle>Stripe Configuration</CardTitle>
							<CardDescription>
								Configure your Stripe API keys for payment processing. Keys are stored encrypted and
								never shown after saving.
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="stripe-pk">Publishable key</Label>
						<Input
							id="stripe-pk"
							value={stripePublishableKey}
							onChange={(e) => setStripePublishableKey(e.target.value)}
							placeholder="pk_live_... or pk_test_..."
							className="font-mono text-xs"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="stripe-sk">Secret key</Label>
						<Input
							id="stripe-sk"
							type="password"
							value={stripeSecretKey}
							onChange={(e) => setStripeSecretKey(e.target.value)}
							placeholder="sk_live_... or sk_test_..."
							className="font-mono text-xs"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="stripe-wh">Webhook secret</Label>
						<Input
							id="stripe-wh"
							type="password"
							value={stripeWebhookSecret}
							onChange={(e) => setStripeWebhookSecret(e.target.value)}
							placeholder="whsec_..."
							className="font-mono text-xs"
						/>
					</div>

					{testResult && (
						<div
							className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${testResult.ok ? "bg-green-500/10 text-green-700 dark:text-green-400" : "bg-destructive/10 text-destructive"}`}
						>
							{testResult.ok ? (
								<RiCheckLine className="size-4" />
							) : (
								<RiCloseLine className="size-4" />
							)}
							{testResult.message}
						</div>
					)}

					<div className="flex gap-2">
						<Button onClick={handleSave} disabled={saving}>
							{saving ? "Saving..." : "Save configuration"}
						</Button>
						<Button variant="outline" onClick={handleTestConnection} disabled={testing}>
							{testing ? "Testing..." : "Test connection"}
						</Button>
						<a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer">
							<Button variant="ghost" size="sm">
								<RiExternalLinkLine className="size-4 mr-1" />
								Stripe Dashboard
							</Button>
						</a>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Webhook Endpoint</CardTitle>
					<CardDescription>
						Configure this URL in your Stripe dashboard under Developers &rarr; Webhooks.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex items-center gap-2">
						<code className="flex-1 rounded bg-muted px-3 py-2 text-xs font-mono">
							{window.location.origin}/api/v1/webhooks/stripe
						</code>
						<Button
							variant="outline"
							size="sm"
							onClick={() => {
								navigator.clipboard.writeText(`${window.location.origin}/api/v1/webhooks/stripe`);
								toast.info("Copied to clipboard");
							}}
						>
							Copy
						</Button>
					</div>
					<p className="mt-2 text-xs text-muted-foreground">
						Events to listen for: <code>customer.subscription.created</code>,{" "}
						<code>customer.subscription.updated</code>, <code>customer.subscription.deleted</code>,{" "}
						<code>invoice.payment_failed</code>
					</p>
				</CardContent>
			</Card>
		</div>
	);
}

// ── Mail Tab ─────────────────────────────────────────────────────────

function MailTab() {
	const [smtpHost, setSmtpHost] = useState("");
	const [smtpPort, setSmtpPort] = useState("587");
	const [smtpUser, setSmtpUser] = useState("");
	const [smtpPass, setSmtpPass] = useState("");
	const [fromEmail, setFromEmail] = useState("");
	const [fromName, setFromName] = useState("");
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);
	const [testResult, setTestResult] = useState<{
		ok: boolean;
		message: string;
	} | null>(null);

	async function handleTestMail() {
		setTesting(true);
		setTestResult(null);
		try {
			const res = await fetch("/api/v1/admin/mail/test", {
				method: "POST",
				credentials: "include",
			});
			const data = await res.json().catch(() => null);
			if (res.ok) {
				setTestResult({
					ok: true,
					message: data?.message ?? "Test email sent",
				});
			} else {
				setTestResult({
					ok: false,
					message: data?.detail ?? "Failed to send test email",
				});
			}
		} catch {
			setTestResult({ ok: false, message: "Failed to reach server" });
		} finally {
			setTesting(false);
		}
	}

	async function handleSave() {
		setSaving(true);
		try {
			const res = await fetch("/api/v1/admin/mail/config", {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({
					smtp_host: smtpHost || undefined,
					smtp_port: smtpPort ? Number.parseInt(smtpPort, 10) : undefined,
					smtp_user: smtpUser || undefined,
					smtp_pass: smtpPass || undefined,
					from_email: fromEmail || undefined,
					from_name: fromName || undefined,
				}),
			});
			if (res.ok) {
				toast.success("Mail configuration saved");
				setSmtpPass("");
			} else {
				const data = await res.json().catch(() => null);
				toast.error(data?.detail ?? "Failed to save");
			}
		} finally {
			setSaving(false);
		}
	}

	return (
		<div className="space-y-6 max-w-2xl">
			<Card>
				<CardHeader>
					<div className="flex items-center gap-2">
						<RiMailLine className="size-5 text-muted-foreground" />
						<div>
							<CardTitle>Mail Configuration</CardTitle>
							<CardDescription>
								SMTP settings for transactional emails (Stalwart Mail, Postmark, SendGrid, etc.).
								Credentials are stored encrypted.
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="smtp-host">SMTP host</Label>
							<Input
								id="smtp-host"
								value={smtpHost}
								onChange={(e) => setSmtpHost(e.target.value)}
								placeholder="mail.example.com"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="smtp-port">SMTP port</Label>
							<Input
								id="smtp-port"
								value={smtpPort}
								onChange={(e) => setSmtpPort(e.target.value)}
								placeholder="587"
							/>
						</div>
					</div>
					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="smtp-user">Username</Label>
							<Input
								id="smtp-user"
								value={smtpUser}
								onChange={(e) => setSmtpUser(e.target.value)}
								placeholder="noreply@example.com"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="smtp-pass">Password</Label>
							<Input
								id="smtp-pass"
								type="password"
								value={smtpPass}
								onChange={(e) => setSmtpPass(e.target.value)}
								placeholder="SMTP password"
							/>
						</div>
					</div>
					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="from-email">From email</Label>
							<Input
								id="from-email"
								value={fromEmail}
								onChange={(e) => setFromEmail(e.target.value)}
								placeholder="noreply@example.com"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="from-name">From name</Label>
							<Input
								id="from-name"
								value={fromName}
								onChange={(e) => setFromName(e.target.value)}
								placeholder="My SaaS"
							/>
						</div>
					</div>

					{testResult && (
						<div
							className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${testResult.ok ? "bg-green-500/10 text-green-700 dark:text-green-400" : "bg-destructive/10 text-destructive"}`}
						>
							{testResult.ok ? (
								<RiCheckLine className="size-4" />
							) : (
								<RiCloseLine className="size-4" />
							)}
							{testResult.message}
						</div>
					)}

					<div className="flex gap-2">
						<Button onClick={handleSave} disabled={saving}>
							{saving ? "Saving..." : "Save configuration"}
						</Button>
						<Button variant="outline" onClick={handleTestMail} disabled={testing}>
							{testing ? "Sending..." : "Send test email"}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
